import {
  Close,
  ExpandMore,
  ShoppingCartCheckout,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useFeatures } from "modules/FeatureManagement/service/featureManagement.hooks";
import {
  AuthenticatedImageBackgroundDiv,
} from "modules/UI/components/AuthenticatedImage.component";
import Drawer from "modules/UI/components/Drawer.component";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import SapService from "service/sap.service";
import { createPrCreateRequestBody, extractPrNumberFromResponse, extractSapErrorMessage } from "utils/sap.utils";

export const CartContext = React.createContext<any>({
  cart: [],
  purchaseOrders: [],
});

export const useCart = () => {
  const context = React.useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = React.useState<any>([]);

  const purchaseOrders = useMemo(() => {
    return cart.reduce((acc, { material, quantity }, materialCartIndex) => {
      const key =
        material.supplierCode +
        material.strategicBuyerEmailAddress +
        material.incoTerms +
        material.currencyCode +
        material.waysOfBuying.join(",");
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({ material: { ...material, materialCartIndex }, quantity });
      return acc;
    }, {});
  }, [cart]);
  return (
    <CartContext.Provider
      value={{
        cart,
        purchaseOrders,
        clearAll: () => setCart([]),
        addMaterial: (material, quantity) => {
          setCart((prev) => [...prev, { material, quantity }]);
        },
        updateMaterial: (index, newData) => {
          setCart((prev) =>
            prev.map((item, i) => ({
              material: i === index ? newData : item.material,
              quantity: item.quantity,
            }))
          );
        },
        updateQuantity: (index, newData) => {
          setCart((prev) =>
            prev.map((item, i) => ({
              quantity: i === index ? newData : item.quantity,
              material: item.material,
            }))
          );
        },
        removeMaterial: (index) => {
          setCart((prev) => prev.filter((_, i) => i !== index));
        },
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const CartButton = (props: { onClick: () => void }) => {
  const { cart } = useCart();
  return (
    <Tooltip title="My Basket" arrow>
      <IconButton onClick={() => props.onClick()}>
        <Badge badgeContent={cart.length} color="secondary">
          <ShoppingCartOutlined />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};

export const CartDrawer = (props: { open: boolean; onClose: () => void }) => {
  const { cart, updateQuantity, removeMaterial, purchaseOrders, clearAll } = useCart();
  const theme = useTheme();
  const features = useFeatures();
  const navigate = useNavigate();
  
  const [processingOrders, setProcessingOrders] = React.useState<Set<number>>(new Set());

  const removeOrderFromProcessing = (orderIndex: number) => {
    setProcessingOrders(prev => {
      const newSet = new Set(prev);
      newSet.delete(orderIndex);
      return newSet;
    });
  };

  const { mutate: createPurchaseRequest } = useMutation({
    mutationKey: ["createPurchaseRequest"],
    mutationFn: async ({ requestBody, orderIndex }: { requestBody: any; orderIndex: number }) => {
      return SapService.createPurchaseRequest(requestBody);
    },
    onSuccess: (response, variables) => {
      const prNumber = extractPrNumberFromResponse(response);
      
      if (prNumber) {
        toast.success(
          `Purchase requisition created successfully! PR Number: ${prNumber}`,
          {
            autoClose: 5000,
          }
        );
      } else {
        toast.success("Purchase requisition created successfully!");
      }
      
      removeOrderFromProcessing(variables.orderIndex);
      clearAll();
      props.onClose();
    },
    onError: (error: any, variables) => {
      const errorMessage = extractSapErrorMessage(error);
      toast.error(errorMessage);
      removeOrderFromProcessing(variables.orderIndex);
    }
  });

  const handleCheckout = (order: any[], orderIndex: number) => {
    // Add this order to processing state
    setProcessingOrders(prev => new Set(prev).add(orderIndex));
    
    const requestBody = createPrCreateRequestBody(order);
    createPurchaseRequest({ requestBody, orderIndex });
  };
  return (
    <Drawer
      title="My Basket"
      open={props.open}
      onClose={props.onClose}
    >
      <Grid container spacing={2}>
        {cart.length === 0 && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{ width: "100%" }}
              action={
                <Button
                  onClick={() => {
                    props.onClose();
                    navigate("/browse");
                  }}
                >
                  Browse Materials
                </Button>
              }
            >
              <Typography variant="body1" textAlign={"center"}>
                Your local cart looks empty.
              </Typography>
            </Alert>
          </Grid>
        )}
        {Object.values(purchaseOrders).map((order: any, orderIndex) => (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={`Purchase Order #${orderIndex + 1}`}
                titleTypographyProps={{ variant: "h6" }}
              />
              <CardContent sx={{ padding: 0 }}>
                <List>
                  {order.map(({ material, quantity }, materialIndex) => (
                    <ListItem>
                      <ListItemAvatar sx={{ pr: 2 }}>
                        <AuthenticatedImageBackgroundDiv
                          style={{
                            width: 65,
                            height: 65,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            borderRadius: theme.shape.borderRadius,
                            boxShadow: theme.shadows[1],
                          }}
                          src={
                            material.mediaFiles?.find(
                              (file) =>
                                !file.isQuality &&
                                file.mimeType.indexOf("image") > -1
                            )?.url
                          }
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={material.shortDescription}
                        secondary={
                          <Grid container spacing={2} alignItems={"flex-end"}>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                fullWidth
                                variant="standard"
                                // label={"Quantity"}
                                value={quantity}
                                onChange={(e) => {
                                  updateQuantity(
                                    material.materialCartIndex,
                                    e.target.value
                                  );
                                }}
                                slotProps={{
                                  input: {
                                    inputMode: "numeric",
                                    inputProps: {
                                      pattern: "[0-9]*",
                                      textAlign: "right",
                                    },
                                    endAdornment: <>{material.unitOfMeasure}</>,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={6}
                              sx={{ transform: "translateX(26px)" }}
                            >
                              <Tooltip
                                placement="bottom-end"
                                arrow
                                title={`${quantity} ${
                                  material.unitOfMeasure
                                } x ${
                                  [...material.priceRanges]
                                    .sort((a, b) => {
                                      return a.quantity < b.quantity ? 1 : -1;
                                    })
                                    .find((r) => quantity >= r.quantity)?.price
                                } ${material.currencyCode}`}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textAlign: "right",
                                    fontSize: 18,
                                  }}
                                >
                                  {Intl.NumberFormat(undefined, {
                                    style: "currency",
                                    currency: material.currencyCode,
                                  }).format(
                                    quantity *
                                      [...material.priceRanges]
                                        .sort((a, b) => {
                                          return a.quantity < b.quantity
                                            ? 1
                                            : -1;
                                        })
                                        .find((r) => quantity >= r.quantity)
                                        ?.price
                                  )}
                                </Typography>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          removeMaterial(material.materialCartIndex)
                        }
                        sx={{ alignSelf: "flex-start", fontSize: 16 }}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              {/* <CardActionArea> */}
              <CardActions sx={{ padding: 2 }}>
                <Box
                  display="flex"
                  width={"100%"}
                  justifyContent="space-between"
                  alignItems={"center"}
                >
                  <Tooltip title="Checkout in Easy Request">
                    <Button
                      disabled={cart.length === 0 || processingOrders.has(orderIndex)}
                      startIcon={processingOrders.has(orderIndex) ? <CircularProgress size={20} /> : <ShoppingCartCheckout />}
                      size="large"
                      variant="contained"
                      color="primary"
                      onClick={() => handleCheckout(order, orderIndex)}
                    >
                      {processingOrders.has(orderIndex) ? "Processing..." : "Checkout"}
                    </Button>
                  </Tooltip>
                  <Box width={200} textAlign={"right"}>
                    <Typography variant="body1">Grand Total:</Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: "right",
                        fontSize: 18,
                        fontWeight: "bold",
                      }}
                    >
                      {Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: order[0].material.currencyCode,
                      }).format(
                        order.reduce((total, { material, quantity }) => {
                          return (
                            total +
                            quantity *
                              [...material.priceRanges]
                                .sort((a, b) => {
                                  return a.quantity < b.quantity ? 1 : -1;
                                })
                                .find((r) => quantity >= r.quantity)?.price
                          );
                        }, 0)
                      )}
                    </Typography>
                  </Box>
                </Box>
              </CardActions>
              {/* </CardActionArea> */}
            </Card>
          </Grid>
        ))}
        {features["reset-flags"] && (
          <Grid item xs={12}>
            <Accordion sx={{ width: "100%" }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Debug Data</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {Object.keys(purchaseOrders).join(", ")}
                <Box height={400} sx={{ overflow: "auto" }}>
                  <code>
                    <pre>{JSON.stringify(purchaseOrders, null, 3)}</pre>
                  </code>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </Drawer>
  );
};
